import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { InlineEditBar, InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { PhotoFileInput } from "@/components/ui/photo-file-input"
import { Select } from "@/components/ui/select"
import { RichTextField } from "@/components/ui/rich-text-field"
import { PhotoLibraryDialog } from "@/components/photo-library/PhotoLibraryDialog"
import { PhotoSelectField, type PhotoSelection } from "@/components/photo-library/PhotoSelectField"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

export type SiteRoom = {
  id: number
  name: string
  description: string | null
  capacity: number | null
  size_label: string | null
  features: string[]
  display_order: number
  url: string | null
}

export type SiteData = {
  id?: number
  name: string
  slug: string
  position?: number
  region: string
  address: string
  maps_url: string
  latitude: number | null
  longitude: number | null
  opening_hours: string | null
  features: { en: string; es: string }[]
  description_en: string
  description_es: string
  status: string
  rooms?: SiteRoom[]
}

// Room mutations are out-of-band requests that redirect back to the same edit
// screen. preserveState keeps unsaved site-form edits alive across them (the
// rooms list still refreshes because props update).
const ROOM_VISIT_OPTIONS = { preserveScroll: true, preserveState: true } as const

function roomsPath(siteId: number) {
  return `/admin/website/locations/${siteId}/rooms`
}

// Rails deep-munges an empty array param to nil, which would leave the
// features untouched — [""] is how "no features" reaches the model, whose
// setter drops the blank.
function featureList(text: string) {
  const list = text.split(",").map((f) => f.trim()).filter(Boolean)
  return list.length > 0 ? list : [ "" ]
}

function RoomCard({
  siteId,
  room,
  library,
}: {
  siteId: number
  room: SiteRoom
  library: LibraryPhoto[]
}) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)

  const fields = useInlineRecord({
    record: {
      name: room.name,
      description: room.description ?? "",
      capacity: room.capacity ? String(room.capacity) : "",
      size_label: room.size_label ?? "",
      features: room.features.join(", "),
    },
    url: `${roomsPath(siteId)}/${room.id}`,
    param: "room",
  })
  const v = fields.values

  const nameState = fields.state("name")
  const descriptionState = fields.state("description")
  const capacityState = fields.state("capacity", {
    serialize: (value) => (value.trim() === "" ? null : Number(value)),
  })
  const sizeState = fields.state("size_label")
  const featuresState = fields.state("features", { serialize: featureList })

  // The photo goes up as soon as it is chosen or picked — a picture needs no
  // confirming, you can see it landed.
  function uploadPhoto(file: File) {
    inlinePatch(`${roomsPath(siteId)}/${room.id}`, { room: { photo: file } }, { forceFormData: true })
  }

  function chooseFromLibrary(photo: LibraryPhoto) {
    inlinePatch(`${roomsPath(siteId)}/${room.id}`, { room: { photo_id: photo.id } })
  }

  function removeRoom() {
    router.delete(`${roomsPath(siteId)}/${room.id}`, ROOM_VISIT_OPTIONS)
  }

  return (
    <div className="overflow-hidden rounded-lg border border-hairline">
      {room.url ? (
        <img src={room.url} alt={room.name} className="aspect-video w-full object-cover" />
      ) : (
        <div className="flex aspect-video items-center justify-center bg-surface text-ink-muted text-xs">
          No photo yet
        </div>
      )}
      <div className="space-y-2 p-3">
        {editing ? (
          <div className="space-y-2">
            <InlineField state={nameState}>
              <Input
                value={v.name}
                onChange={(e) => fields.set("name", e.target.value)}
                onKeyDown={nameState.keys}
                placeholder="Room name"
              />
            </InlineField>
            <InlineField state={descriptionState}>
              <Input
                value={v.description}
                onChange={(e) => fields.set("description", e.target.value)}
                onKeyDown={descriptionState.keys}
                placeholder="Description"
              />
            </InlineField>
            <div className="grid gap-2 sm:grid-cols-2">
              <InlineField state={capacityState}>
                <Input
                  type="number"
                  min={1}
                  value={v.capacity}
                  onChange={(e) => fields.set("capacity", e.target.value)}
                  onKeyDown={capacityState.keys}
                  placeholder="Seats (e.g. 3)"
                />
              </InlineField>
              <InlineField state={sizeState}>
                <Input
                  value={v.size_label}
                  onChange={(e) => fields.set("size_label", e.target.value)}
                  onKeyDown={sizeState.keys}
                  placeholder="Size (e.g. 18 m²)"
                />
              </InlineField>
            </div>
            <InlineField state={featuresState}>
              <Input
                value={v.features}
                onChange={(e) => fields.set("features", e.target.value)}
                onKeyDown={featuresState.keys}
                placeholder="Features, comma-separated (e.g. Fireplace, Double-glazed windows)"
              />
            </InlineField>
            <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(false)}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-ink-display">{room.name}</p>
            {room.description && <p className="text-xs text-ink-muted">{room.description}</p>}
            {(room.capacity || room.size_label) && (
              <p className="text-xs text-ink-muted">
                {[room.capacity && `Seats up to ${room.capacity}`, room.size_label]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
            {room.features.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {room.features.map((f) => (
                  <span key={f} className="rounded-full bg-surface px-2 py-0.5 text-xs text-ink-muted">
                    {f}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!editing && (
            <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit details
            </Button>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
            Choose from library
          </Button>
          <PhotoFileInput onSelect={(files) => files[0] && uploadPhoto(files[0])} />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="text-danger-display hover:border-danger hover:bg-danger-faded"
            onClick={removeRoom}
          >
            Remove
          </Button>
        </div>
      </div>
      <PhotoLibraryDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        library={library}
        onSelect={chooseFromLibrary}
      />
    </div>
  )
}

export function RoomsSection({
  siteId,
  rooms,
  photoLibrary,
}: {
  siteId: number
  rooms: SiteRoom[]
  photoLibrary: LibraryPhoto[]
}) {
  const [newRoomName, setNewRoomName] = React.useState("")
  const [newRoomDescription, setNewRoomDescription] = React.useState("")
  const [newRoomPhoto, setNewRoomPhoto] = React.useState<PhotoSelection>({ kind: "unchanged" })
  const [newRoomResetKey, setNewRoomResetKey] = React.useState(0)

  function addRoom() {
    if (!newRoomName) return
    const fd = new FormData()
    fd.append("room[name]", newRoomName)
    fd.append("room[description]", newRoomDescription)
    if (newRoomPhoto.kind === "upload") fd.append("room[photo]", newRoomPhoto.file)
    if (newRoomPhoto.kind === "library") fd.append("room[photo_id]", String(newRoomPhoto.id))
    router.post(roomsPath(siteId), fd, {
      ...ROOM_VISIT_OPTIONS,
      onSuccess: () => {
        setNewRoomName("")
        setNewRoomDescription("")
        setNewRoomPhoto({ kind: "unchanged" })
        setNewRoomResetKey((k) => k + 1)
      },
    })
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        The individual rooms shown on this location's public page, each with its own photo.
        A room's details save a field at a time; its photo saves as soon as you choose it.
      </p>
      {rooms.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.map((room) => (
            <RoomCard key={room.id} siteId={siteId} room={room} library={photoLibrary} />
          ))}
        </div>
      )}

      {/* Add new room */}
      <div className="rounded-lg border border-dashed border-hairline p-4">
        <p className="mb-3 text-sm font-medium text-ink-display">Add a room</p>
        <div className="space-y-3">
          <Input
            placeholder="Room name (e.g. Room 1)"
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
          />
          <Input
            placeholder="Description"
            value={newRoomDescription}
            onChange={(e) => setNewRoomDescription(e.target.value)}
          />
          <PhotoSelectField
            key={newRoomResetKey}
            library={photoLibrary}
            value={newRoomPhoto}
            onChange={setNewRoomPhoto}
            inputId="new-room-photo"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!newRoomName}
            onClick={addRoom}
          >
            Add room
          </Button>
        </div>
      </div>
    </div>
  )
}

export function SiteForm({
  site,
  actionUrl,
  method,
}: {
  site: SiteData
  actionUrl: string
  method: "post" | "patch"
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isNew = method === "post"

  const fields = useInlineRecord({
    record: {
      name: site.name,
      slug: site.slug,
      position: String(site.position ?? 0),
      region: site.region,
      address: site.address,
      maps_url: site.maps_url,
      // Held as typed text, not numbers: a half-typed "-2." is a valid thing
      // to have in a field and not a valid number, and clearing one must send
      // "" so the pair can be unset rather than silently kept.
      latitude: site.latitude?.toString() ?? "",
      longitude: site.longitude?.toString() ?? "",
      opening_hours: site.opening_hours ?? "",
      features: site.features.length > 0 ? site.features : [ { en: "", es: "" } ],
      description_en: site.description_en,
      description_es: site.description_es,
      status: site.status,
    },
    url: actionUrl,
    param: "site",
    enabled: !isNew,
  })
  const v = fields.values

  const [processing, setProcessing] = React.useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      {
        site: {
          ...v,
          position: v.position.trim(),
          latitude: v.latitude.trim(),
          longitude: v.longitude.trim(),
          features: v.features.filter((f) => f.en || f.es),
        },
      },
      { preserveScroll: true, onFinish: () => setProcessing(false) },
    )
  }

  function addFeature() {
    fields.set("features", [ ...v.features, { en: "", es: "" } ])
  }

  function removeFeature(index: number) {
    fields.set("features", v.features.filter((_, i) => i !== index))
  }

  function updateFeature(index: number, lang: "en" | "es", value: string) {
    fields.set("features", v.features.map((f, i) => (i === index ? { ...f, [lang]: value } : f)))
  }

  const nameState = fields.state("name")
  const regionState = fields.state("region")
  const slugState = fields.state("slug")
  const positionState = fields.state("position", { serialize: (value) => value.trim() })
  const statusState = fields.state("status")
  const addressState = fields.state("address")
  const mapsState = fields.state("maps_url")
  // The map pin is a pair — half of it puts the marker in the Atlantic and the
  // model refuses it — so either tick sends both halves.
  const coordinates = (draft: typeof v) => ({
    latitude: draft.latitude.trim(),
    longitude: draft.longitude.trim(),
  })
  const latitudeState = fields.state("latitude", { serialize: (x) => x.trim(), with: coordinates })
  const longitudeState = fields.state("longitude", { serialize: (x) => x.trim(), with: coordinates })
  const hoursState = fields.state("opening_hours")
  const featuresState = fields.state("features", {
    serialize: (list) => list.filter((f) => f.en || f.es),
  })
  const descEnState = fields.state("description_en")
  const descEsState = fields.state("description_es")

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic info */}
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="Site name" htmlFor="name" error={errors.name} state={nameState}>
            <Input
              id="name"
              value={v.name}
              onChange={(e) => fields.set("name", e.target.value)}
              onKeyDown={nameState.keys}
              required
              placeholder="e.g. Prestwich"
              aria-invalid={!!errors.name}
            />
          </InlineField>

          <InlineField label="Region" htmlFor="region" state={regionState}>
            <Input
              id="region"
              value={v.region}
              onChange={(e) => fields.set("region", e.target.value)}
              onKeyDown={regionState.keys}
              placeholder="e.g. Prestwich, Manchester"
            />
          </InlineField>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="URL slug" htmlFor="slug" error={errors.slug} state={slugState}>
            <Input
              id="slug"
              value={v.slug}
              onChange={(e) => fields.set("slug", e.target.value)}
              onKeyDown={slugState.keys}
              placeholder="auto-generated from name"
              aria-invalid={!!errors.slug}
            />
          </InlineField>

          {/* Where the location sits in the nav's Locations menu and the
              footer — lowest first. */}
          <InlineField
            label="Menu order"
            htmlFor="position"
            error={errors.position}
            state={positionState}
          >
            <Input
              id="position"
              type="number"
              min={0}
              step={1}
              value={v.position}
              onChange={(e) => fields.set("position", e.target.value)}
              onKeyDown={positionState.keys}
              aria-invalid={!!errors.position}
            />
          </InlineField>

          <InlineField label="Status" htmlFor="status" state={statusState}>
            <Select id="status" value={v.status} onChange={(e) => fields.set("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </InlineField>
        </div>

        <InlineField label="Address" htmlFor="address" state={addressState}>
          <Input
            id="address"
            value={v.address}
            onChange={(e) => fields.set("address", e.target.value)}
            onKeyDown={addressState.keys}
            placeholder="Full address"
          />
        </InlineField>

        <InlineField label="Google Maps URL" htmlFor="maps_url" state={mapsState}>
          <Input
            id="maps_url"
            type="url"
            value={v.maps_url}
            onChange={(e) => fields.set("maps_url", e.target.value)}
            onKeyDown={mapsState.keys}
            placeholder="https://maps.google.com/..."
          />
        </InlineField>

        {/* The map pin. Both or neither — half a pair puts the marker in the
            Atlantic, and the model refuses it. A postcode lookup
            (postcodes.io, or right-click a spot in Google Maps) gives these. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField
            label="Latitude"
            htmlFor="latitude"
            hint="Saved together with the longitude."
            state={latitudeState}
          >
            <Input
              id="latitude"
              value={v.latitude}
              onChange={(e) => fields.set("latitude", e.target.value)}
              onKeyDown={latitudeState.keys}
              placeholder="53.741411"
            />
          </InlineField>
          <InlineField
            label="Longitude"
            htmlFor="longitude"
            hint="Saved together with the latitude."
            state={longitudeState}
          >
            <Input
              id="longitude"
              value={v.longitude}
              onChange={(e) => fields.set("longitude", e.target.value)}
              onKeyDown={longitudeState.keys}
              placeholder="-2.016915"
            />
          </InlineField>
        </div>

        <InlineField label="Opening hours" htmlFor="opening_hours" state={hoursState}>
          <Input
            id="opening_hours"
            value={v.opening_hours}
            onChange={(e) => fields.set("opening_hours", e.target.value)}
            onKeyDown={hoursState.keys}
            placeholder="e.g. 8am – 10pm, Monday to Sunday"
          />
        </InlineField>
      </section>

      {/* Features */}
      <section className="space-y-4">
        <h3>Features</h3>
        <p className="text-sm text-ink-muted">
          Short selling-point phrases shown on the /{v.slug || "…"} page (e.g. "Great
          natural light"). The list is stored as one thing, so adding, editing and removing
          phrases all wait for the same tick.
        </p>
        <div className="space-y-3">
          {v.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="grid flex-1 gap-2 sm:grid-cols-2">
                <Input
                  value={f.en}
                  onChange={(e) => updateFeature(i, "en", e.target.value)}
                  onKeyDown={featuresState.keys}
                  placeholder="English (e.g. Great natural light)"
                />
                <Input
                  value={f.es}
                  onChange={(e) => updateFeature(i, "es", e.target.value)}
                  onKeyDown={featuresState.keys}
                  placeholder="Spanish (e.g. Gran luz natural)"
                />
              </div>
              <button
                type="button"
                onClick={() => removeFeature(i)}
                className="mt-2 text-ink-muted hover:text-danger-display"
                aria-label="Remove feature"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <InlineEditBar state={featuresState} />
        <Button type="button" variant="secondary" size="sm" onClick={addFeature}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add feature
        </Button>
      </section>

      {/* Description */}
      <section className="space-y-4">
        <h3>Description</h3>
        <p className="text-sm text-ink-muted">
          Shown on the /{v.slug || "…"} page.
        </p>
        <InlineField label="Description (English)" state={descEnState}>
          <RichTextField
            key={`desc-en-${descEnState.version}`}
            defaultValue={descEnState.value}
            onChange={(md) => fields.set("description_en", md)}
            placeholder="Describe this location in English…"
          />
        </InlineField>
        <InlineField label="Description (Spanish)" state={descEsState}>
          <RichTextField
            key={`desc-es-${descEsState.version}`}
            defaultValue={descEsState.value}
            onChange={(md) => fields.set("description_es", md)}
            placeholder="Describa esta ubicación en español…"
          />
        </InlineField>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              Add location
            </Button>
            <Button type="button" variant="ghost" asChild>
              <Link href="/admin/website">Cancel</Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink-muted">
            Each change is kept when you tick it — there's nothing else to save.
          </p>
        )}
      </div>
    </form>
  )
}
